// =============================================================================
// components/glass/ContextMenu.tsx — long-press floating translucent menu (DARK).
//
// Reference: the IG DM context-menu screenshot — a long-press on a row dims the
// whole screen, pushes the background back, and floats a compact dark-glass menu
// of icon+label rows (with a destructive red row) anchored at the touch origin.
//
// Apple-design SKILL alignment:
//   §7  anchored origin — the menu scales up FROM the trigger point (transformOrigin
//        approximated by translating the scale pivot toward the anchor corner).
//   §12 dim-to-focus — a scrim dims + a subtle scale pushes the background back.
//   §4  behavior over animation — spring in/out via Animated.spring using the
//        shared animationTokens (gentle settle; a touch of bounce on enter only).
//   §14 reduced motion/transparency handled INSIDE here (never in GlassSurface).
//
// Built ONLY from existing primitives: GlassSurface (mode='dark') for the menu
// body, Icon for glyphs. No new native deps.
// =============================================================================
import React, { useEffect, useRef } from 'react';
import {
  Modal, View, Text, Pressable, Animated, StyleSheet,
  useWindowDimensions, LayoutChangeEvent, StyleProp, ViewStyle,
} from 'react-native';
import { Ionicons, IoniconName } from '../ui/Icon';
import GlassSurface from './GlassSurface';
import { glassTokens, glassColorTokens } from './tokens';
import { colors, spacing, fonts, animationTokens } from '../../constants/theme';
import { useReducedMotionTransparency } from '../ui/useReducedMotionTransparency';

export interface ContextMenuAction {
  key: string;
  label: string;
  icon?: IoniconName;
  destructive?: boolean;
  onPress: () => void;
}

/** Screen-space rect of the element that was long-pressed (the anchor). */
export interface ContextMenuAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ContextMenuProps {
  visible: boolean;
  onClose: () => void;
  actions: ContextMenuAction[];
  /** Where the menu grows from (the long-pressed row's frame). Optional — falls
   *  back to screen center. Pass e.g. the measured layout of the pressed item. */
  anchor?: ContextMenuAnchor | null;
  /** Optional header content shown above the actions (e.g. a message preview). */
  header?: React.ReactNode;
  menuWidth?: number;
  style?: StyleProp<ViewStyle>;
}

const GUTTER = spacing.lg;
const ROW_H = 48;

export default function ContextMenu({
  visible, onClose, actions, anchor, header, menuWidth = 240, style,
}: ContextMenuProps) {
  const { width: screenW, height: screenH } = useWindowDimensions();
  const { reduceMotion, reduceTransparency } = useReducedMotionTransparency();

  const anim = useRef(new Animated.Value(0)).current;   // 0 hidden → 1 shown
  const [menuH, setMenuH] = React.useState(0);

  useEffect(() => {
    if (visible) {
      if (reduceMotion) {
        // §14: cross-fade only, no scale/vestibular motion.
        Animated.timing(anim, {
          toValue: 1, duration: animationTokens.duration.base, useNativeDriver: true,
        }).start();
      } else {
        // §4: spring in with a small overshoot (the press "carried" momentum).
        Animated.spring(anim, {
          toValue: 1,
          useNativeDriver: true,
          damping: animationTokens.springBouncy.damping,
          stiffness: animationTokens.springBouncy.stiffness,
          mass: animationTokens.springBouncy.mass,
        }).start();
      }
    } else {
      Animated.timing(anim, {
        toValue: 0,
        duration: reduceMotion ? animationTokens.duration.base : animationTokens.duration.fast,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, reduceMotion, anim]);

  // Position the menu near the anchor, clamped to the screen. Grows from the
  // anchor's leading edge; flips above if it would overflow the bottom.
  const estH = menuH || (actions.length * ROW_H + (header ? 64 : 0) + spacing.md * 2);
  let left = anchor ? anchor.x : (screenW - menuWidth) / 2;
  left = Math.max(GUTTER, Math.min(left, screenW - menuWidth - GUTTER));

  let top: number;
  if (anchor) {
    const below = anchor.y + anchor.height + spacing.sm;
    top = below + estH > screenH - GUTTER
      ? Math.max(GUTTER, anchor.y - estH - spacing.sm)   // flip above
      : below;
  } else {
    top = (screenH - estH) / 2;
  }

  // §7 anchored origin — pivot the scale toward the anchor corner so the menu
  // reads as emerging FROM the trigger, not the screen center. Animated views
  // scale about their own center, so we offset by half the size during the
  // grow, decaying to 0 at rest.
  const anchorPivotX = anchor ? (anchor.x < screenW / 2 ? -menuWidth / 2 : menuWidth / 2) : 0;
  const anchorPivotY = anchor ? (top >= (anchor.y) ? -estH / 2 : estH / 2) : 0;

  const scale = anim.interpolate({ inputRange: [0, 1], outputRange: [reduceMotion ? 1 : 0.82, 1] });
  const pivotTX = anim.interpolate({ inputRange: [0, 1], outputRange: [anchorPivotX, 0] });
  const pivotTY = anim.interpolate({ inputRange: [0, 1], outputRange: [anchorPivotY, 0] });

  // §12 dim-to-focus scrim (skip decorative heavy dim under reduce-transparency).
  const scrimOpacity = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, reduceTransparency ? 0.72 : 0.58],
  });

  const onMenuLayout = (e: LayoutChangeEvent) => setMenuH(e.nativeEvent.layout.height);

  // Frostier fill when transparency is reduced (§14).
  const menuFill = reduceTransparency
    ? 'rgba(18,18,30,0.98)'
    : glassColorTokens.dark.surface.sheet;

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Close menu">
        <Animated.View style={[styles.scrim, { opacity: scrimOpacity }]} pointerEvents="none" />
      </Pressable>

      <Animated.View
        onLayout={onMenuLayout}
        style={[
          styles.menuWrap,
          { left, top, width: menuWidth, opacity: anim },
          { transform: [{ translateX: pivotTX }, { translateY: pivotTY }, { scale }] },
          style,
        ]}
        pointerEvents="box-none"
      >
        <GlassSurface
          mode="light"
          variant="sheet"
          radius={glassTokens.radius.lg}
          surfaceColor={menuFill}
          contentStyle={styles.menuContent}
        >
          {header ? <View style={styles.header}>{header}</View> : null}
          {actions.map((a, i) => (
            <Pressable
              key={a.key}
              onPress={() => { onClose(); a.onPress(); }}
              style={({ pressed }) => [
                styles.row,
                i > 0 && styles.rowDivider,
                pressed && styles.rowPressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel={a.label}
            >
              <Text style={[styles.label, a.destructive && styles.labelDestructive]} numberOfLines={1}>
                {a.label}
              </Text>
              {a.icon ? (
                <Ionicons
                  name={a.icon}
                  size={19}
                  color={a.destructive ? colors.error : 'rgba(255,255,255,0.92)'}
                />
              ) : null}
            </Pressable>
          ))}
        </GlassSurface>
      </Animated.View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  scrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#05050A' },
  menuWrap: { position: 'absolute' },
  menuContent: { paddingVertical: spacing.xs },
  header: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: glassColorTokens.dark.border,
  },
  row: {
    height: ROW_H,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
  },
  rowDivider: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: glassColorTokens.dark.borderSubtle,
  },
  rowPressed: { backgroundColor: 'rgba(255,255,255,0.06)' },
  label: { ...fonts.semibold, fontSize: 15, color: 'rgba(255,255,255,0.92)', flexShrink: 1 },
  labelDestructive: { color: colors.error },
});
